import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

import { MOCK_ACTIVE_RULES_GRAPH, USE_MOCK_ACTIVE_RULES } from "./dev-mocks";
import type { ApiErrorEnvelope, RuleGraph } from "./types";

/**
 * React Query cache key for `GET /compliance/withdrawals/rules`.
 *
 * Exposed as a stable constant (not inlined into the hook) so
 * `usePublishRules` can invalidate the cache after a write — the
 * whole point of using React Query here.
 */
export const GET_ACTIVE_RULES_QUERY_KEY = [
  "GET_ACTIVE_WITHDRAWAL_RULES",
] as const;

/**
 * Special-cased so the empty state can render without treating the
 * (expected) "nothing published yet" case as an error. Every other
 * failure propagates through `useQuery` and surfaces as `isError`.
 */
async function fetchActiveRules(): Promise<RuleGraph | null> {
  const instance = getPrivateInstance();
  try {
    const response = await instance.get<{ data: RuleGraph }>(
      API_ROUTES.GET_ACTIVE_WITHDRAWAL_RULES,
    );
    return response?.data?.data ?? null;
  } catch (err) {
    const axiosErr = err as AxiosError<ApiErrorEnvelope>;
    // 404 (`code: "not_found"`) is not an error — it's the empty
    // state the FE renders when no graph has ever been published.
    // Distinguishing it from 5xx keeps `isError` meaningful in
    // consumers.
    if (axiosErr.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Fetches the currently active withdrawal-rules graph.
 *
 * Success paths:
 * - Graph exists → `data` is the `RuleGraph`.
 * - Nothing published yet (BE 404) → `data` is `null`. Consumers
 *   should render an empty state, not an error.
 *
 * Error paths (surfaced via `isError` / `error`):
 * - 403 → permission missing. `error.details.permission` names the
 *   required key (`compliance.withdrawals.get_rule`).
 * - 503 → the active pointer references a missing version
 *   (BE-side data integrity issue). Rare — retry is unlikely to
 *   help, escalate to BE.
 * - 5xx (other) → transient; React Query will retry per the default
 *   policy of the caller's `QueryClient`.
 */
export function useGetActiveRules(): UseQueryResult<RuleGraph | null> {
  return useQuery<RuleGraph | null>({
    queryKey: GET_ACTIVE_RULES_QUERY_KEY,
    queryFn: fetchActiveRules,
    // 60s stale window. The graph is essentially static between
    // publishes, so refetch on every focus/mount would just waste
    // requests; a one-minute freshness bar is a good default.
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}
